import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FileItemDto } from '@common/generated-api/models/file-item-dto';
import { UserCreateForm } from '@common/generated-api/models/user-create-form';
import { UserDto } from '@common/generated-api/models/user-dto';
import { UserUpdateForm } from '@common/generated-api/models/user-update-form';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AppService } from '@shared/services/app.service';
import { CurrentUserService } from '@shared/services/current-user.service';
import { StringsService } from '@shared/services/strings.service';
import { UsersService } from '@shared/services/users.service';
import { UserRoles } from '@shared/types/api-types';
import { ICompany } from '@shared/types/common.types';
import { ControlsFrom, Merge } from '@shared/types/type.utils';
import { CustomValidators } from '@shared/utils/validators.utils';
import { environment } from '../../../../../environments/environment';
import { SnackbarService } from '@shared/services/snackbar.service';
import { ResponseUserDto } from '@common/generated-api/models/response-user-dto';
import { ParseApiValidationError, ServerValidationErrors } from '@shared/utils/app.errors';

@UntilDestroy()
@Component({
  selector: 'app-user-edit',
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.scss'],
})
export class UserEditComponent implements OnInit {
  /**
   * if userId provided, user will be loaded from server
   */
  @Input() userId: number;
  @Input() user: Merge<UserDto, { password?: string }>;
  /**
   * if company provided, user will be created as user of this company
   */
  @Input() company: ICompany;
  /**
   * force to create ADMIN_RESPONSIBLE user
   */
  @Input() forceTitle: string;
  @Input() isResponsible: boolean;
  @Input() canEditResponsibleRole = true;
  @Input() asModal: boolean = false;
  @Output() done = new EventEmitter<UserDto>();
  @Output() canceled = new EventEmitter();

  form: FormGroup<ControlsFrom<UserCreateForm>>;
  isResponsibleControl = new FormControl<boolean>(false);
  hideIsResponsibleSwitcher = false;
  newPassword: string;
  userCreated: boolean;
  formTitle: string;
  isProd = environment.production;
  private typePDUPerformer: boolean;

  constructor(
    private appService: AppService,
    private currentUserService: CurrentUserService,
    public stringsService: StringsService,
    private usersService: UsersService,
    private snackbarService: SnackbarService,
  ) {
    this.form = new FormGroup<ControlsFrom<UserCreateForm>>({
      name: new FormControl<string>('', Validators.required),
      username: new FormControl<string>(
        '',
        [Validators.required, CustomValidators.loginFormat]
      ),
      email: new FormControl<string>('', Validators.email),
      notes: new FormControl<string>(''),
      phone: new FormControl<string>(null),
      phoneCountry: new FormControl<string>('--'),
      picture: new FormControl<number>(null),
      position: new FormControl<string>(''),
      role: new FormControl<UserCreateForm['role']>('ADMIN_COMMON')
    });
  }

  ngOnInit(): void {
    if (!this.user?.id && !this.userId) {
      this.formTitle = $localize`Добавить сотрудника`;
    } else {
      this.formTitle = $localize`Редактировать сотрудника`;
    }
    if (this.userId) {
      this.usersService.getUser(this.userId).subscribe({
        next: (data) => {
          this.user = data.result;
          this.initForm();
        },
      });
    } else {
      this.initForm();
    }
  }

  initForm() {
    if (this.user?.id) {
      this.form.patchValue({
        ...(this.user as UserCreateForm),
        picture: this.user.picture?.id || null
      });
      this.isResponsibleControl.setValue(
        this.user?.role && (this.user.role as UserRoles) === UserRoles.ADMIN_RESPONSIBLE,
      );
    }
    if (this.isResponsible) {
      this.isResponsibleControl.setValue(true);
      this.hideIsResponsibleSwitcher = true;
    }
    if (!this.canEditResponsibleRole) {
      this.hideIsResponsibleSwitcher = true;
    }
  }

  createUser() {
    const companyProp = this.company
      ? { [this.company.type]: this.company.id }
      : { ...this.currentUserService.currentCompanyType$.getValue() };
    const body: UserCreateForm = {
      ...companyProp,
      ...(this.form.value as UserCreateForm),
      role: this.isResponsibleControl.value ? 'ADMIN_RESPONSIBLE' : 'ADMIN_COMMON',
    };

    if (this.typePDUPerformer) {
      body.role = 'PDU_PERFORMER';
    }
    this.usersService.createUser(body).subscribe({
      next: (data: ResponseUserDto) => {
        this.user = data.result;
        this.userCreated = true;
      },
      error: err => {
        const errorParams = ParseApiValidationError(err);
        if (errorParams.type === ServerValidationErrors.duplicate) {
          this.form.controls.username.setErrors({
            duplicate: {
              item: $localize`Пользователь`,
              params: $localize`логином`
            }
          });
        } else if (errorParams.type === ServerValidationErrors.invalid_param
          && errorParams.target === 'phone') {
            this.form.controls.phone.setErrors({
              onlyNumber: true
            });
        } else {
          this.snackbarService.showError($localize`Произошла ошибка при отправке запроса на сервер.`);
        }
      }
    });
  }

  updateUser() {
    const body: UserUpdateForm = {
      ...(this.form.value as UserUpdateForm),
      role: this.isResponsibleControl.value ? 'ADMIN_RESPONSIBLE' : 'ADMIN_COMMON',
    };

    this.usersService.updateUser(this.user.id, body).subscribe({
      next: (data) => {
        this.user = data.result;
        this.done.emit(data.result);
      },
    });
  }

  pictureChanged(event: FileItemDto) {
    this.form.controls.picture.setValue(event.id);
  }

  save() {
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.user?.id) {
      this.updateUser();
    } else {
      this.createUser();
    }
  }

  passwordCopied() {
    this.snackbarService.showMessage($localize`Пароль скопирован`);
  }

  pictureRemoved() {
    this.form.controls.picture.setValue(null);
  }

  debug() {
    console.log(this.form);
    console.log(this.form.controls['phone']);
  }

  pduPerformer() {
    if (!environment.production) {
      this.typePDUPerformer = true;
    }
  }
}
